import { useContext, useMemo, useEffect, useCallback, createElement as _createElement } from 'react';
import { scaleBand } from '@visx/scale';
import DataContext from "../../../context/DataContext.js";
import getScaleBandwidth from "../../../utils/getScaleBandwidth.js";
import getScaleBaseline from "../../../utils/getScaleBaseline.js";
import isValidNumber from "../../../typeguards/isValidNumber.js";
import { BARGROUP_EVENT_SOURCE, XYCHART_EVENT_SOURCE } from "../../../constants.js";
import useSeriesEvents from "../../../hooks/useSeriesEvents.js";
import findNearestGroupDatum from "../../../utils/findNearestGroupDatum.js";
import getChildrenAndGrandchildrenWithProps from "../../../utils/getChildrenAndGrandchildrenWithProps.js";
import { jsx as _jsx } from "react/jsx-runtime";
export default function BaseBarGroup(_ref) {
  let {
    children,
    padding = 0.1,
    sortBars,
    BarsComponent,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    enableEvents = true
  } = _ref;
  const {
    colorScale,
    dataRegistry,
    horizontal,
    registerData,
    unregisterData,
    xScale,
    yScale
  } = useContext(DataContext);
  const barSeriesChildren = useMemo(() => getChildrenAndGrandchildrenWithProps(children), [children]);

  // extract data keys from child series
  const dataKeys = useMemo(() => barSeriesChildren.map(child => child.props.dataKey ?? '').filter(key => key), [barSeriesChildren]);

  // register all child data
  useEffect(() => {
    const dataToRegister = barSeriesChildren.map(child => {
      const {
        dataKey: key,
        data,
        xAccessor,
        yAccessor
      } = child.props;
      return {
        key,
        data,
        xAccessor,
        yAccessor
      };
    });
    registerData(dataToRegister);
    return () => unregisterData(dataKeys);
  }, [registerData, unregisterData, barSeriesChildren, dataKeys]);

  // create group scale
  const groupScale = useMemo(() => scaleBand({
    domain: sortBars ? [...dataKeys].sort(sortBars) : dataKeys,
    range: [0, getScaleBandwidth(horizontal ? yScale : xScale)],
    padding
  }), [sortBars, dataKeys, xScale, yScale, horizontal, padding]);
  const findNearestDatum = useCallback(params => findNearestGroupDatum(params, groupScale, horizontal), [groupScale, horizontal]);
  const ownEventSourceKey = `${BARGROUP_EVENT_SOURCE}-${dataKeys.join('-')}}`;
  const eventEmitters = useSeriesEvents({
    dataKey: dataKeys,
    enableEvents,
    findNearestDatum,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    source: ownEventSourceKey,
    allowedSources: [XYCHART_EVENT_SOURCE, ownEventSourceKey]
  });
  const xZeroPosition = useMemo(() => xScale ? getScaleBaseline(xScale) : 0, [xScale]);
  const yZeroPosition = useMemo(() => yScale ? getScaleBaseline(yScale) : 0, [yScale]);
  const registryEntries = dataKeys.map(key => dataRegistry.get(key));

  // if scales and data are not available in the registry, bail
  if (registryEntries.some(entry => entry == null) || !xScale || !yScale || !colorScale) {
    return null;
  }
  const barThickness = getScaleBandwidth(groupScale);
  const barSeries = registryEntries.map(_ref2 => {
    let {
      xAccessor,
      yAccessor,
      data,
      key
    } = _ref2;
    const getLength = d => horizontal ? (xScale(xAccessor(d)) ?? NaN) - xZeroPosition : (yScale(yAccessor(d)) ?? NaN) - yZeroPosition;
    const getGroupPosition = horizontal ? d => yScale(yAccessor(d)) ?? NaN : d => xScale(xAccessor(d)) ?? NaN;
    const withinGroupPosition = groupScale(key) ?? 0;
    const getX = horizontal ? d => xZeroPosition + Math.min(0, getLength(d)) : d => getGroupPosition(d) + withinGroupPosition;
    const getY = horizontal ? d => getGroupPosition(d) + withinGroupPosition : d => yZeroPosition + Math.min(0, getLength(d));
    const getWidth = horizontal ? d => Math.abs(getLength(d)) : () => barThickness;
    const getHeight = horizontal ? () => barThickness : d => Math.abs(getLength(d));
    // get props from child BarSeries, if available
    const childBarSeries = barSeriesChildren.find(child => child.props.dataKey === key);
    const {
      colorAccessor,
      radius,
      radiusAll,
      radiusBottom,
      radiusLeft,
      radiusRight,
      radiusTop
    } = childBarSeries?.props || {};
    return {
      key,
      radius,
      radiusAll,
      radiusBottom,
      radiusLeft,
      radiusRight,
      radiusTop,
      bars: data.map((bar, index) => {
        const barX = getX(bar);
        if (!isValidNumber(barX)) return null;
        const barY = getY(bar);
        if (!isValidNumber(barY)) return null;
        const barWidth = getWidth(bar);
        if (!isValidNumber(barWidth)) return null;
        const barHeight = getHeight(bar);
        if (!isValidNumber(barHeight)) return null;
        return {
          key: `${key}-${index}`,
          x: barX,
          y: barY,
          width: barWidth,
          height: barHeight,
          fill: colorAccessor?.(bar, index) ?? colorScale(key)
        };
      }).filter(bar => bar)
    };
  });
  return /*#__PURE__*/_jsx("g", {
    className: "visx-bar-group",
    children: barSeries.map(series => series && /*#__PURE__*/_createElement(BarsComponent, {
      horizontal: horizontal,
      xScale: xScale,
      yScale: yScale,
      ...series,
      ...eventEmitters,
      key: series.key
    }))
  });
}